import {
  React,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import "./drawerTemplate.scss";
import {
  FiHome,
  FiInbox,
  FiServer,
  FiArchive,
  FiUsers,
  FiMenu,
  FiX,
  FiChevronRight,
  FiLogOut,
} from "react-icons/fi";
import { useHistory, useLocation } from "react-router-dom";
import Logo from "../containers/DashboardPage/Logo/index";
import { MdErrorOutline } from "react-icons/md";

import {
  Input,
  Divider,
  TextArea,
  Button,
  FinanceCard,
  SearchBar,
  OrderCard,
  NewOrderCard,
  Label,
  FileExplorer,
} from "../components";
import { UserContext } from "../Context/UserContext";
import { useAnalyticsContext, TAGS } from "../Context/Analytics";
import { BiRupee } from "react-icons/bi";
import { GoGraph } from "react-icons/go";
import { HiOutlineTruck } from "react-icons/hi2";
import { IoStorefrontOutline } from "react-icons/io5";
import { RiPassPendingLine } from "react-icons/ri";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

export default (OldComponent, setSortedProjects, projects) => {
  const NewComponent = (props) => {
    const history = useHistory();
    const location = useLocation();
    const userContext = useContext(UserContext);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const { record } = useAnalyticsContext();
    const activeItemRef = useRef(null);
    const menuContainerRef = useRef(null);
    const prevPathRef = useRef(location.pathname);
    const expandTimeoutRef = useRef(null);
    const collapseTimeoutRef = useRef(null);

    // Check for screen sizes and set up event listeners
    useEffect(() => {
      const handleResize = () => {
        const width = window.innerWidth;
        setWindowWidth(width);
        const isMobileScreen = width < 768;
        setIsMobile(isMobileScreen);
        if (isMobileScreen) {
          setIsExpanded(false);
        }
      };

      // Initialize
      handleResize();

      // Add resize listener with throttling for better performance
      let resizeTimer;
      const throttledResize = () => {
        if (!resizeTimer) {
          resizeTimer = setTimeout(() => {
            resizeTimer = null;
            handleResize();
          }, 100);
        }
      };

      window.addEventListener("resize", throttledResize);

      // Cleanup
      return () => {
        window.removeEventListener("resize", throttledResize);
        clearTimeout(resizeTimer);
        clearTimeout(expandTimeoutRef.current);
        clearTimeout(collapseTimeoutRef.current);
      };
    }, []);

    // Auto-scroll to active menu item when path changes
    useEffect(() => {
      if (
        activeItemRef.current &&
        menuContainerRef.current &&
        prevPathRef.current !== location.pathname
      ) {
        const container = menuContainerRef.current;
        const activeItem = activeItemRef.current;

        if (!container || !activeItem) return;

        // Calculate the scroll position to center the active item
        const containerHeight = container.clientHeight;
        const itemHeight = activeItem.clientHeight;
        const itemTop = activeItem.offsetTop;

        const targetScrollPosition =
          itemTop - containerHeight / 2 + itemHeight / 2;

        // Use requestAnimationFrame for smooth scrolling
        requestAnimationFrame(() => {
          container.scrollTo({
            top: Math.max(0, targetScrollPosition),
            behavior: "auto", // Changed to auto for better performance
          });
        });

        // Update previous path
        prevPathRef.current = location.pathname;
      }
    }, [location.pathname]);

    // Toggle drawer for mobile
    const toggleDrawer = () => {
      setIsExpanded(!isExpanded);
    };

    // Handle mouse events for desktop hover
    const handleMouseEnter = () => {
      if (!isMobile && !isExpanded) {
        setIsExpanded(true);
      }
    };

    const handleMouseLeave = () => {
      if (!isMobile && isExpanded) {
        setIsExpanded(false);
      }
    };

    // Get first letter of firm name for compact view
    const firmInitial = userContext.user.Firm
      ? userContext.user.Firm.charAt(0).toUpperCase()
      : "";

    // Adjust menu items based on available screen space
    const getAdjustedName = (item) => {
      if (!isExpanded) return item.shortName || item.name;

      // For responsive naming at different zoom levels
      if (windowWidth < 850 && item.name.length > 10) {
        return item.shortName || item.name.substring(0, 10) + "...";
      }

      return item.name;
    };

    // Navigation menu configuration
    const menuItems = [
      {
        name: "Home",
        icon: <FiHome />,
        path: "/",
        access: ["All"],
        onClick: () => {
          history.push("/");
          record("@home_icon_click_success", {
            description: "home icon click success",
          });
        },
      },
      {
        name: "Users",
        icon: <FiUsers />,
        path: "/Analytics",
        access: ["Admin", "Owner", "Finance", "Project"],
        onClick: () => {
          history.push("/Analytics");
          record("@analytics_icon_click_success", {
            description: "analytics icon click success",
          });
        },
      },
      {
        name: "Suggestive Items",
        shortName: "Items",
        icon: <FiServer />,
        path: "/SuggestiveItems",
        access: ["Admin", "Owner"],
        onClick: () => {
          history.push("/SuggestiveItems");
          record("@suggestive_items_icon_click_success", {
            description: "suggestive items icon click success",
          });
        },
      },
      {
        name: "Finance Approvals",
        shortName: "Finance",
        icon: <BiRupee />,
        path: "/FinanceRecords",
        access: ["Admin", "Owner", "Finance", "Project"],
        onClick: () => {
          history.push("/FinanceRecords");
          record("@financeApprovals request_icon_click_success", {
            description: "financeApprovals icon click success",
          });
        },
      },
      {
        name: "Billing",
        icon: <ReceiptLongIcon style={{ fontSize: 20 }} />,
        path: "/Billing",
        access: ["All"],
        onClick: () => {
          history.push("/Billing");
          record("@billing_icon_click_success", {
            description: "billing icon click success",
          });
        },
      },
      {
        name: "Analytics",
        icon: <GoGraph />,
        path: "/POAnalytics",
        access: ["All"],
        onClick: () => {
          history.push("/POAnalytics");
        },
      },
      {
        name: "Material Requests",
        shortName: "Material",
        icon: <RiPassPendingLine />,
        path: "/MaterialRequest",
        access: ["All"],
        onClick: () => {
          history.push("/MaterialRequest");
        },
      },
      {
        name: "Timeline",
        icon: <RiPassPendingLine />,
        path: "/Timeline",
        access: ["All"],
        onClick: () => {
          history.push("/Timeline");
        },
      },
      {
        name: "Inventory",
        icon: <IoStorefrontOutline />,
        path: "/Inventory",
        access: ["All"],
        onClick: () => {
          history.push("/Inventory");
        },
      },
      {
        name: "Assets",
        icon: <HiOutlineTruck />,
        path: "/Vehicles",
        access: ["All"],
        onClick: () => {
          history.push("/Vehicles");
        },
      },
      {
        name: "Quality",
        icon: <MdErrorOutline />,
        path: "/Quality",
        access: ["All"],
        onClick: () => {
          history.push("/Quality");
          record("@quality_icon_click_success", {
            description: "quality icon click success",
          });
        },
      },
    ];

    // Check if user has access to menu item
    const hasAccess = (accessList) => {
      return (
        accessList.includes("All") ||
        accessList.includes(userContext.user.accountType)
      );
    };

    // Handle logout
    const handleLogout = () => {
      record("@exit_icon_click_success", {
        description: "Exit icon click success",
      });
      history.push("/");
      props.onAuthChanged(null);
      localStorage.clear();
      sessionStorage.clear();
    };

    return (
      <div id="drawerTemplate">
        {/* Mobile menu toggle button */}
        {isMobile && (
          <div
            style={{
              position: "fixed",
              top: "1rem",
              left: isExpanded ? "17rem" : "1rem",
              zIndex: 1001,
              transition: "left 0.5s ease",
              background: "linear-gradient(145deg, #2f4858, #1a2730)",
              borderRadius: "50%",
              width: "2.75rem",
              height: "2.75rem",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
              border: "2px solid rgba(255,255,255,0.08)",
            }}
            onClick={toggleDrawer}
          >
            {isExpanded ? (
              <FiX color="#fff" size={18} />
            ) : (
              <FiMenu color="#fff" size={18} />
            )}
          </div>
        )}

        {/* Main drawer */}
        <div
          id="dpDrawer"
          className={isExpanded ? "expanded" : "collapsed"}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {/* Inner scrollable container */}
          <div className="drawer-inner">
            {/* Header with profile */}
            <div className="drawer-header">
              <div id="dpdpic" className={isExpanded ? "" : "compact"}>
                {isExpanded ? (
                  <abbr title="Click to upload Image">
                    <Logo />
                  </abbr>
                ) : (
                  <div className="firmInitialCircle">{firmInitial}</div>
                )}
              </div>

              {isExpanded && (
                <div className="user-info">
                  <div id="dpdCompanyName">
                    <Label
                      content={userContext.user.Firm || ""}
                      size="1rem"
                      textAlign="center"
                      fontWeight="bold"
                      style={{
                        width: "100%",
                        textAlign: "center",
                        display: "block",
                      }}
                    />
                  </div>
                  <div id="dpdEmail">
                    <Label
                      content={userContext.user.email || ""}
                      size="small"
                      style={{
                        width: "100%",
                        textAlign: "center",
                        display: "block",
                      }}
                    />
                  </div>
                  <div id="dpdEmail">
                    <Label
                      content={userContext.user.userName || ""}
                      size="small"
                      style={{
                        width: "100%",
                        textAlign: "center",
                        display: "block",
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Menu items */}
            <div className="menu-items-container" ref={menuContainerRef}>
              {/* Navigation menu items */}
              {menuItems.map(
                (item, index) =>
                  hasAccess(item.access) && (
                    <div
                      key={index}
                      className={`dpdMenuItem ${
                        location.pathname === item.path
                          ? "dpdMenuItemActive"
                          : ""
                      } ${!isExpanded ? "iconOnly" : ""}`}
                      onClick={item.onClick}
                      ref={
                        location.pathname === item.path ? activeItemRef : null
                      }
                    >
                      {!isExpanded ? (
                        <div>
                          {item.icon}
                          <p>{item.shortName || item.name}</p>
                        </div>
                      ) : (
                        <>
                          {item.icon}
                          <div
                            style={{
                              flex: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {getAdjustedName(item)}
                          </div>
                          {location.pathname === item.path && (
                            <FiChevronRight
                              size={16}
                              style={{ flexShrink: 0 }}
                            />
                          )}
                        </>
                      )}
                    </div>
                  )
              )}

              {/* Logout button */}
              <div
                className={`dpdMenuItem logout-item ${
                  !isExpanded ? "iconOnly" : ""
                }`}
                onClick={handleLogout}
              >
                {!isExpanded ? (
                  <div>
                    <FiLogOut />
                    <p>Logout</p>
                  </div>
                ) : (
                  <>
                    <FiLogOut />
                    <div style={{ flex: 1 }}>Logout</div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main content area */}
        <div id="drawerContent">
          <OldComponent {...props} />
        </div>
      </div>
    );
  };

  return NewComponent;
};
